<template>
    <modal
        v-if="show"
        class="complianceModal"
        :title="titleModal"
        :form="form"
        :submitText="'Save'"
        :cancelText="'Cancel'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy,
        }"
        :submitDisabled="submitDisabled"
        @closed="handleClosed"
        @submit="handleSubmit"
    >
        <div v-if="loading" class="complianceModal__content">
            <basic-loader center aria-label="CNAMEdit" />
        </div>
        <div v-else class="complianceModal__content">
            <div class="complianceModal__title">
                Register phone numbers for CNAM {{ item.name }}
            </div>

            <div class="complianceModal__column">
                <form-dropdown
                    id="dropdown-phone_numbers"
                    ref="select"
                    :label="'Select your Salesmsg number that you want to add a Caller ID to'"
                    :error="form.errors.get('phone_numbers')"
                    :hideLabelColon="true"
                    :settings="dropdownSettings"
                    dropdown-class="selectize-contacts"
                    @input="updateSelected"
                />
            </div>
        </div>
    </modal>
</template>

<script>
import Form from 'core/Form';
import { mapState } from 'pinia';
import stores from 'store/modules';

export default {
    data: () => ({
        loading: true,
        form: new Form({
            cnam_id: null,
            phone_numbers: [],
        }),
        currentPhoneNumbers: [],
        deletePhoneNumbers: [],
        addPhoneNumbers: [],
    }),

    computed: {
        item() {
            return this.showCNAMItem;
        },

        show() {
            return this.showCNAMEditModal;
        },

        titleModal() {
            return `Enable CNAM Caller ID ${this.item.name}`;
        },

        numbers() {
            return [...this.CNAMCallerIdNumbers, ...this.CNAMCallerFreeNumbers];
        },

        numberIds() {
            return this.CNAMCallerIdNumbers.map((item) => item.id);
        },

        dropdownSettings() {
            return {
                valueField: 'id',
                searchField: ['label'],
                options: this.numbers,
                highlight: true,
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
                maxItems: null,
                dropdownParent: 'body',
                plugins: ['remove_button'],
            };
        },

        submitDisabled() {
            return Boolean(![...this.deletePhoneNumbers, ...this.addPhoneNumbers].length);
        },

        ...mapState(stores.ComplianceProfile, ['CNAMCallerFreeNumbers', 'CNAMCallerIdNumbers']),
        ...mapState(stores.CallingStore, ['showCNAMItem', 'showCNAMEditModal']),
    },

    watch: {
        show(value) {
            if (value) {
                this.init();
            }
        },
    },

    methods: {
        async init() {
            await this.reset();
            await this.initNumbers();
            await this.initForm();
        },

        async initNumbers() {
            try {
                this.loading = true;

                await Promise.all([
                    this.$store.dispatch('fetchCNAMCallerFreeNumbers'),
                    this.$store.dispatch('fetchCNAMIdNumber', this.item.id),
                ]);

                this.currentPhoneNumbers = this.CNAMCallerIdNumbers.map((item) => item.id);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'initNumbers');
            } finally {
                await this.$delay(200);

                this.loading = false;
            }
        },

        async initForm() {
            this.form.cnam_id = this.item.id;
            this.form.phone_numbers = this.numberIds;

            await this.$delay(200);

            if (this.$refs.select.dropdown) {
                this.$refs.select.dropdown.addItems(this.numberIds);
            }
        },

        reset() {
            this.loading = false;
            this.form.cnam_id = null;
            this.form.phone_numbers = [];
            this.form.reset();
            this.currentPhoneNumbers = [];
            this.deletePhoneNumbers = [];
            this.addPhoneNumbers = [];
        },

        handleClosed() {
            this.$store.dispatch('handleShowCNAMEditModal', {
                status: false,
                item: null,
            });
            this.$emit('closed');
        },

        async handleSubmit() {
            try {
                this.form.busy = true;

                const params = {
                    cnam_id: this.item.id,
                    phone_numbers: [],
                };

                if (this.deletePhoneNumbers.length) {
                    params.phone_numbers = this.deletePhoneNumbers;

                    await this.$store.dispatch('deleteCNAMNumbers', params);
                }

                if (this.addPhoneNumbers.length) {
                    params.phone_numbers = this.addPhoneNumbers;

                    await this.$store.dispatch('addCNAMNumbers', params);
                }

                this.form.successful = true;

                setTimeout(() => {
                    this.handleClosed();
                }, 1500);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSubmit');
            } finally {
                this.form.busy = false;
            }
        },

        updateSelected(ids) {
            ids = ids.map((id) => +id);

            this.form.phone_numbers = ids.map((id) => +id);

            this.deletePhoneNumbers = this.currentPhoneNumbers
                .map((id) => {
                    const exist = ids.find((o) => o === id);

                    return exist ? undefined : id;
                })
                .filter((item) => item);

            this.addPhoneNumbers = ids
                .map((id) => {
                    const exist = this.currentPhoneNumbers.find((o) => o === id);

                    return !exist ? id : undefined;
                })
                .filter((item) => item);
        },

        dropdownOption(item, escape) {
            return `<div class="selectize-contact">
                <div class="selectize-contact-info">
                    <strong>${item.label}</strong>
                </div>
            </div>`;
        },

        dropdownItem(item, escape) {
            return `<div class="selectize-contact tag is-small is-blue">
                        ${item.label}
                    </div>`;
        },
    },
};
</script>
